import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { associateMultiplexPanel } from 'api/multiplexPanels';
import { PanelSlideSummary } from 'components/StudyDashboard/MultiplexPanels/PanelSlideSummary';
import { MultiplexPanelUnassignedGroup, MultiplexPanelView } from 'interfaces/multiplexPanels';
import { StainType } from 'interfaces/stainType';
import { compact, filter, find, flatMap, includes, join, map, startCase, uniq } from 'lodash';
import React, { useState } from 'react';
import { invalidateMarkerAssignmentQueries } from 'utils/queryHooks/markerAssignmentQueries';
import { initializePanelChannels, PanelChannelDraft } from './panelForm';

interface Props {
  group: MultiplexPanelUnassignedGroup;
  panels: MultiplexPanelView[];
  studyId: string;
  labId?: string;
  stains: StainType[];
  canEdit: boolean;
  sourceSlideId?: string;
  refreshing?: boolean;
  onDefineNewPanel: (source: MultiplexPanelView, channels: PanelChannelDraft[], slideIds: string[]) => void;
  onAssociated: (panelId: string, requiresReanalysis: boolean) => void;
}

export const UnassignedSlides = ({
  group,
  panels,
  studyId,
  labId,
  stains,
  canEdit,
  sourceSlideId,
  refreshing,
  onDefineNewPanel,
  onAssociated,
}: Props) => {
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState(() => (sourceSlideId ? [sourceSlideId] : map(group.slides, 'slideId')));
  const [targetPanelId, setTargetPanelId] = useState<string>();
  const [splitValues, setSplitValues] = useState<Record<string, string>>({});
  const [requestError, setRequestError] = useState<string>();
  const selectedIds = filter(uniq(selection), (slideId) => Boolean(find(group.slides, { slideId })));
  const candidates = filter(group.compatiblePanels || [], (candidate) =>
    Boolean(
      find(
        panels,
        (panel) =>
          panel.panelId === candidate.panelId &&
          panel.saved &&
          panel.revision === candidate.revision &&
          panel.channels.length > 0
      )
    )
  );
  const candidate = find(candidates, { panelId: targetPanelId });
  const target = candidate ? find(panels, { panelId: candidate.panelId }) : undefined;
  const splits = [
    { key: 'biopsyType', label: 'Biopsy type', values: uniq(compact(map(group.slides, 'biopsyType'))) },
    {
      key: 'indications',
      label: 'Indication',
      values: uniq(compact(flatMap(group.slides, (slide) => slide.indications || []))),
    },
    {
      key: 'subIndications',
      label: 'Sub-indication',
      values: uniq(compact(flatMap(group.slides, (slide) => slide.subIndications || []))),
    },
  ].filter((split) => split.values.length > 1);
  const filteredSlides = filter(group.slides, (slide) =>
    splits.every((split) => {
      const value = splitValues[split.key];
      if (!value || !includes(split.values, value)) return true;
      return split.key === 'biopsyType'
        ? slide.biopsyType === value
        : includes(slide[split.key as 'indications' | 'subIndications'] || [], value);
    })
  );
  const source: MultiplexPanelView = {
    panelId: `unassigned-${group.groupId}`,
    saved: false,
    channels: group.channels,
    slides: group.slides,
  };
  const association = useMutation({
    mutationKey: ['multiplex_panel_mutations', studyId],
    mutationFn: () => {
      if (!candidate || !selectedIds.length || refreshing)
        throw new Error('Refresh and select an available panel and slides.');
      return associateMultiplexPanel(studyId, {
        panelId: candidate.panelId,
        revision: candidate.revision,
        slideIds: selectedIds,
      });
    },
    onSuccess: async (result) => {
      setRequestError(undefined);
      await invalidateMarkerAssignmentQueries(queryClient);
      await queryClient.invalidateQueries({ queryKey: ['study', studyId] });
      onAssociated(result.membership.panelId, result.requiresReanalysis);
    },
    onError: (error: any) => {
      const detail = error?.response?.body?.detail;
      setRequestError(
        Array.isArray(detail)
          ? join(detail, ' ')
          : typeof detail === 'string'
          ? detail
          : 'Association could not be confirmed. Your selection is retained.'
      );
    },
  });
  const busy = association.isLoading || Boolean(refreshing);
  const labels = target
    ? map(
        target.channels,
        (channel) =>
          channel.saved?.displayName ||
          find(stains, { id: channel.saved?.stainTypeId })?.displayName ||
          channel.saved?.analysisName ||
          join(channel.identifiers.sourceNames, ' / ')
      )
    : [];
  return (
    <Stack spacing={1.5} aria-label={`Unassigned group ${group.groupId}`}>
      <Typography variant="subtitle2">
        {group.slides.length} {group.slides.length === 1 ? 'slide' : 'slides'} without a compatible panel
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {map(splits, (split) => (
          <TextField
            key={split.key}
            select
            size="small"
            label={split.label}
            value={includes(split.values, splitValues[split.key]) ? splitValues[split.key] : ''}
            disabled={busy}
            sx={{ minWidth: 160 }}
            onChange={(event) => setSplitValues((current) => ({ ...current, [split.key]: event.target.value }))}
          >
            <MenuItem value="">All</MenuItem>
            {map(split.values, (value) => (
              <MenuItem key={value} value={value}>
                {split.key === 'biopsyType' ? startCase(value) : value}
              </MenuItem>
            ))}
          </TextField>
        ))}
        {filteredSlides.length > 0 && (
          <FormControlLabel
            label={`Select all ${filteredSlides.length} matching slides`}
            control={
              <Checkbox
                disabled={busy}
                checked={filteredSlides.every((slide) => includes(selectedIds, slide.slideId))}
                indeterminate={
                  filteredSlides.some((slide) => includes(selectedIds, slide.slideId)) &&
                  !filteredSlides.every((slide) => includes(selectedIds, slide.slideId))
                }
                onChange={(_event, checked) =>
                  setSelection((current) =>
                    checked
                      ? uniq([...current, ...map(filteredSlides, 'slideId')])
                      : filter(current, (slideId) => !find(filteredSlides, { slideId }))
                  )
                }
              />
            }
          />
        )}
      </Stack>
      <PanelSlideSummary
        panel={{ ...source, slides: filteredSlides }}
        labId={labId}
        selectedSlideIds={selectedIds}
        disabled={busy}
        onSelectionChange={(slideId, checked) =>
          setSelection((current) => (checked ? uniq([...current, slideId]) : filter(current, (id) => id !== slideId)))
        }
      />
      {candidates.length ? (
        <Autocomplete
          options={candidates}
          value={candidate || null}
          disabled={!canEdit || busy}
          isOptionEqualToValue={(option, value) =>
            option.panelId === value.panelId && option.revision === value.revision
          }
          getOptionLabel={(option) =>
            `${option.panelId.replace('panel-', 'Panel ')} · ${
              option.match === 'exact' ? 'same channel order' : 'mapped channel order'
            }`
          }
          onChange={(_event, value) => {
            setTargetPanelId(value?.panelId);
            setRequestError(undefined);
          }}
          renderInput={(params) => <TextField {...params} size="small" label="Existing panel" />}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          Define a new panel for the selected slides.
        </Typography>
      )}
      {target && candidate && (
        <Stack spacing={0.5}>
          <Typography variant="body2">Panel settings: {join(labels, ', ')}</Typography>
          {candidate.match === 'reordered' && (
            <Typography variant="caption">
              Channel mapping:{' '}
              {join(
                map(candidate.channelMapping, (panelIndex, slideIndex) => `${slideIndex} → ${panelIndex}`),
                ', '
              )}
            </Typography>
          )}
          {!candidate.currentSettingsMatch && (
            <Typography variant="caption" color="text.secondary">
              These settings differ from the current slide assignments. Association applies this panel&apos;s marker and
              probe mapping.
            </Typography>
          )}
        </Stack>
      )}
      {requestError && <Alert severity="error">{requestError}</Alert>}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <Typography variant="caption" sx={{ flex: 1 }}>
          {selectedIds.length ? `${selectedIds.length} selected` : 'Select at least one slide'}
        </Typography>
        {candidates.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<LinkOutlinedIcon />}
            disabled={!canEdit || busy || !candidate || !selectedIds.length}
            onClick={() => association.mutate()}
          >
            Associate selected slides
          </Button>
        )}
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<AddOutlinedIcon />}
          disabled={!canEdit || busy || !selectedIds.length}
          onClick={() =>
            onDefineNewPanel(
              source,
              initializePanelChannels(
                { ...source, slides: filter(group.slides, (slide) => includes(selectedIds, slide.slideId)) },
                stains,
                sourceSlideId
              ),
              selectedIds
            )
          }
        >
          Define new panel
        </Button>
      </Stack>
    </Stack>
  );
};
